import { capitalize, ErpFrame, OrderFrame } from '@ca/common'
import FrameUtils from '@frame/FrameUtils'
import useOrderValues from '@order/hooks/values/useOrderValues'
import { getOptionColorFromValue } from '@order/steps/types'
import { useConfigConditionFrameCheck } from 'features/config/useConfigCondition'
import { usePriceFormatter } from 'hooks/useCurrency'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

type Settings = {
  hideQty?: boolean
}

export const useFrameDetails = (
  frame: ErpFrame | OrderFrame | undefined,
  settings?: Settings
) => {
  const values = useOrderValues({})
  const frameCheck = useConfigConditionFrameCheck({
    values,
  })
  const config = useRemoteConfig()
  const formatter = usePriceFormatter()
  const { t } = useTranslation(['common'])

  const details = useMemo(() => {
    if (!frame) return []

    const brandName = frame?.attributes.fbrand
    const showBrandName = frameCheck('frames:showBrandName', frame) && brandName

    const hidePrice = frameCheck('frames:hidePrice', frame)

    const catalogColor = getOptionColorFromValue(frame.attributes.fcatalog)
    const showCatalogBadge = frameCheck('frames:showCatalogBadge', frame)

    const price = FrameUtils.getPrice({
      frame,
      values,
      config,
    })

    return [
      showBrandName ? brandName : undefined,
      !hidePrice ? formatter.format(price) : undefined,
      showCatalogBadge && catalogColor ? capitalize(catalogColor) : undefined,
      !settings?.hideQty
        ? t('common:qtyInStore', { count: frame.store_qty })
        : undefined,
    ].filter((v) => v != undefined) as string[]
  }, [frame, frameCheck, formatter, t, settings?.hideQty, config, values])

  return {
    details,
  }
}
